import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal } from 'lucide-react';
import { CATEGORIES } from '../data/mock';
import { fetchProducts } from '../lib/api';
import ProductCard from '../components/ProductCard';
import CaliLetters from '../components/CaliLetters';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCategory = searchParams.get('category');
  const categoryFromUrl = CATEGORIES.some((c) => c.slug === urlCategory) ? urlCategory : 'all';
  const [cat, setCat] = useState(categoryFromUrl);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('default');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts().then((d) => setProducts(d)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setCat(categoryFromUrl);
  }, [categoryFromUrl]);

  const selectCategory = (slug) => {
    setCat(slug);
    setSearchParams(slug === 'all' ? {} : { category: slug });
  };

  const filtered = useMemo(() => {
    let list = products.filter((p) => (cat === 'all' ? true : p.category === cat));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.subtitle.toLowerCase().includes(q));
    }
    if (sort === 'price-asc') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') list = [...list].sort((a, b) => b.price - a.price);
    if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [products, cat, query, sort]);

  return (
    <div className="pt-[74px]">
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0 smoke-red opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-14 text-center">
          <h1 className="font-anton text-4xl lg:text-6xl uppercase">
            <CaliLetters text="NASZE PRODUKTY" />
          </h1>
          <p className="text-white/60 mt-3 max-w-xl mx-auto">Premium THC — żelki, syropy, shoty, tabletki, czekoladki i więcej. Wybierz swoje doznanie.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => selectCategory(c.slug)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${cat === c.slug ? 'btn-green' : 'bg-white/5 text-white/70 border border-white/10 hover:border-[#a6e22e]/50 hover:text-[#a6e22e]'}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Szukaj..." className="pl-9 pr-4 py-2.5 rounded-full bg-black/60 border border-white/15 text-sm outline-none focus:border-[#a6e22e] transition w-full sm:w-52" />
            </div>
            <div className="relative">
              <SlidersHorizontal size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="pl-9 pr-4 py-2.5 rounded-full bg-black/60 border border-white/15 text-sm outline-none focus:border-[#a6e22e] transition appearance-none cursor-pointer">
                <option value="default">Sortuj</option>
                <option value="price-asc">Cena: rosnąco</option>
                <option value="price-desc">Cena: malejąco</option>
                <option value="rating">Najlepiej oceniane</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-white/50 py-20">Ładowanie produktów...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-white/50 py-20">Brak produktów spełniających kryteria.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default Products;
